import React, { useCallback, useContext, useMemo } from 'react';

// DEPENDENCIES
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { apiFetch, doCallback, modalFunctions } from 'functions';

// CONTEXT
import { DropdownContext } from 'core/tools/dropdown/helpers/dropdownContext';
import { useRecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// CORE COMPONENTS
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import DocUprForm from './DocUprForm';

// VARIABLES
const DOCUMUMENT_TYPE_CONVERSIONS = {
  1:  9,
  3:  1,
  5:  13
}

// FUNCTIONS
const handleSubmit = (params, args) => {
  if (!!params.recipient && !params.recipient.email && !params.recipient.phone) return;
  else apiFetch({
    params,
    ...args
  })
}

// MAIN COMPONENT
const DocUprModal = ({
  name,
  docrName = 'docrAttachmentRequest',
  documentType,
  uploadType: uploadTypeProp,
  requestParams = {},
  requestedFor,
  onClick,
  useTemp,
  vehicle: vehicleProp = {},
  ...props
}) => {
  // CONTEXT
  const { setShow } = useContext(DropdownContext) || {};
  const { view } = useRecordView();
  const { recordID, record: { sales_id, ...record } = {} } = useRecordContext() || {};
  const { values = {}, setFieldValue } = useFormikContext();

  // console.log(values, record)

  // MEMOS
  const vehicle = useMemo(
    () => {
      let v = {};
      if (record?.inventory) v = record.inventory;
      else v = !isEmpty(record) ? record : values ?? {};
      return {
        ...v,
        ...vehicleProp
      };
    },
    [record, vehicleProp, values]
  );

  const uploadType = useMemo(
    () => uploadTypeProp || view,
    [uploadTypeProp, view]
  );

  const isInventoryType = uploadType === 'inventory';

  const requestedFromUiRoute = useMemo(
    () => {
      const origin = window.location.origin;
      let pageUrl = `${origin}${window.location.pathname}`;
      if (useTemp) {
        if (isInventoryType) {
          pageUrl = `${origin}/inventory/view/:dailySalesInventoryId`;
        } else {
          pageUrl = `${origin}/sales/view/:salesId`;
        }
      }
      return pageUrl;
    },
    [useTemp, isInventoryType]
  );

  // CALLBACKS
  const handleClick = useCallback(
    e => {
      doCallback(onClick, e);
      setShow(false);
      modalFunctions.add({
        type: 'form',
        title: 'Send Upload Link',
        fields: <DocUprForm
          documentType={documentType}
          uploadType={uploadType}
          record={record}
          vehicle={vehicle}
        />,
        initialValues: {
          document_type: documentType,
          requested_from_ui_route: requestedFromUiRoute,
          ...(isInventoryType ? {
            daily_sales_inventory_id: vehicle.daily_sales_inventory_id,
          } : {
            sales_id
          }),
          requested_for: requestedFor,
          ...requestParams
        },
        args: {
          endpoint: ENDPOINTS.inventory.uploadLink(uploadType, isInventoryType ? vehicle.daily_sales_inventory_id : recordID),
          queryParams: isInventoryType && view !== 'inventory' ? {
            salesId: sales_id,
            documentType: DOCUMUMENT_TYPE_CONVERSIONS[documentType]
          } : undefined,
          onSubmit: (params = {}, args) => {
            const { manual_entry, ...restParams } = params;

            if (useTemp) {
              const { email, phone } = restParams?.recipient ?? {};
              if ([true, 'true'].includes(manual_entry) && !email && !phone) return;

              // Add to form values and exit
              setFieldValue(docrName, [...(get(values, docrName) || []), omit(restParams, 'dummy')]);
              modalFunctions.clear();
              modalFunctions.success('Upload link sent after form is saved');
            } else {
              // Or submit
              handleSubmit(restParams, args);
            }
          },
          loadingMessage: 'Sending upload link',
          successMessage: 'Upload link sent.',
          errorMessage: 'Unable to send upload link.'
        },
        submitButton: {
          label: 'Send',
          icon: 'email',
        },
        spacing: 'sm'
      })
    },
    [
      docrName,
      documentType,
      isInventoryType,
      onClick,
      record,
      recordID,
      requestParams,
      requestedFor,
      requestedFromUiRoute,
      sales_id,
      setFieldValue,
      setShow,
      uploadType,
      useTemp,
      values,
      vehicle,
      view
    ]
  )

  // RENDER
  return (
    <Button
      label="Send Upload Link"
      onClick={handleClick}
      {...props}
    />
  )
}

// EXPORT
export default DocUprModal;
